import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "../../components/layout/PageHeader";
import Table from "../../components/ui/Table";
import RequestActions, {
  ReviewedCell,
  fmtDate,
  selectColumn,
} from "../../components/requests/RequestActions";
import Spinner from "../../components/ui/Spinner";
import EmptyState from "../../components/ui/EmptyState";
import RequestFilters, {
  StatusBadge,
  useRequestFilters,
} from "../../components/requests/RequestFilters";
import {
  BulkActionBar,
  useRowSelection,
} from "../../components/requests/BulkSelection";
import { RejectRemarksModal } from "./BulkRequestApprovals";
import {
  listCandidateRequests,
  approveCandidateRequest,
  rejectCandidateRequest,
} from "../../api/candidateRequests";
import {
  requestsQueryKey,
  requestsCountQueryKey,
} from "../../hooks/useCandidateRequestCount";
import { REQUEST_TYPES } from "../../utils/candidateRequestTypes";

const dash = (v) => v || <span className="text-gray-400">—</span>;

// Stable per-type components: an inline arrow here would remount the modal (losing the typed
// remarks) on every render.
const REJECT_MODALS = Object.fromEntries(
  Object.entries(REQUEST_TYPES).map(([type, meta]) => [
    type,
    (props) => (
      <RejectRemarksModal
        {...props}
        title={`Reject ${meta.label.toLowerCase()} request`}
      />
    ),
  ]),
);

const columnsFor = (type, meta, refetch, selection) => [
  selectColumn(
    selection,
    (r) => r.id,
    (r) => r.status === "pending",
  ),
  {
    key: "candidate",
    label: "Candidate",
    render: (row) => (
      <div>
        <Link
          to={`/candidates/${row.candidate.id}`}
          className="font-medium text-primary hover:underline"
        >
          {row.candidate.full_name}
        </Link>
        <div className="text-xs text-gray-400">
          {row.candidate.reference_no}
        </div>
      </div>
    ),
  },
  {
    key: "vehicle_type",
    label: "Vehicle Type",
    render: (row) => (
      <span className="capitalize">{dash(row.candidate.vehicle_type)}</span>
    ),
  },
  {
    key: "passport_no",
    label: "Passport No",
    render: (row) => dash(row.candidate.passport_no),
  },
  {
    key: "nationality",
    label: "Nationality",
    render: (row) => dash(row.candidate.nationality),
  },
  {
    key: "location",
    label: "Location",
    render: (row) => (
      <span className="capitalize">{dash(row.candidate.location)}</span>
    ),
  },
  {
    key: "stage",
    label: "Current Stage",
    render: (row) => dash(row.candidate.current_step),
  },
  {
    key: "rms_no",
    label: "RMS No",
    render: (row) => dash(row.candidate.rms_no),
  },
  {
    key: "jde_no",
    label: "JDE No",
    render: (row) => dash(row.candidate.jde_no),
  },
  {
    key: "stamping_date",
    label: "Stamping Date",
    render: (row) => dash(row.candidate.stamping_date),
  },
  {
    key: "source",
    label: "Source",
    render: (row) => dash(row.candidate.source),
  },
  {
    key: "contacts",
    label: "Contacts",
    render: (row) => (
      <div className="text-xs">
        <div>UAE: {row.candidate.contact_no || "—"}</div>
        <div className="text-gray-500">
          Home: {row.candidate.home_country_contact_no || "—"}
        </div>
      </div>
    ),
  },
  {
    key: "requested",
    label: "Requested",
    render: (row) => (
      <div>
        {row.requested_by}
        <div className="text-xs text-gray-400">{fmtDate(row.requested_at)}</div>
        {row.note && (
          <div className="text-xs italic text-gray-500">"{row.note}"</div>
        )}
      </div>
    ),
  },
  {
    key: "status",
    label: "Status",
    render: (row) => (
      <div>
        <StatusBadge status={row.status} />
        <ReviewedCell row={row} />
      </div>
    ),
  },
  {
    key: "scheduled",
    label: "Date / Location",
    render: (row) =>
      row.status === "approved" ? (
        <div className="text-xs">
          <div>
            {row.scheduled_date ? fmtDate(row.scheduled_date) : dash(null)}
          </div>
          <div className="text-gray-500">{row.location?.name || "—"}</div>
        </div>
      ) : (
        dash(null)
      ),
  },
  {
    key: "actions",
    label: "",
    render: (row) => (
      <RequestActions
        pending={row.status === "pending"}
        onApprove={() => approveCandidateRequest(type, row.id)}
        onReject={(reason) => rejectCandidateRequest(type, row.id, reason)}
        onResolved={refetch}
        approvedMessage={`${meta.label} request approved.`}
        rejectedMessage={`${meta.label} request rejected.`}
        RejectModal={REJECT_MODALS[type]}
      />
    ),
  },
];

const searchFields = (row) => [
  row.candidate.full_name,
  row.candidate.reference_no,
  row.candidate.passport_no,
  row.candidate.nationality,
  row.candidate.contact_no,
  row.requested_by,
];
const requestedAt = (row) => row.requested_at;

export default function CandidateRequestApprovals({ type }) {
  const meta = REQUEST_TYPES[type];
  const queryClient = useQueryClient();
  const { filters, set, apply, reset, dirty, candidate } = useRequestFilters(
    searchFields,
    requestedAt,
  );
  const query = useQuery({
    queryKey: [...requestsQueryKey(type), filters.status, candidate.params],
    queryFn: () =>
      listCandidateRequests(type, {
        ...candidate.params,
        status: filters.status,
      }),
  });
  const allRows = query.data || [];
  const rows = apply(allRows);
  const selection = useRowSelection(
    rows,
    (r) => r.id,
    (r) => r.status === "pending",
  );

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: requestsQueryKey(type) });
    queryClient.invalidateQueries({ queryKey: requestsCountQueryKey(type) });
  };

  return (
    <div>
      <PageHeader
        title={`${meta.label} Request`}
        description={`${meta.label} requests raised against candidates — approve or reject them here.`}
      />

      <RequestFilters
        candidate={candidate}
        filters={filters}
        set={set}
        reset={reset}
        dirty={dirty}
        total={allRows.length}
        shown={rows.length}
      />

      <BulkActionBar
        selection={selection}
        onApprove={(id) => approveCandidateRequest(type, id)}
        onReject={(id, reason) => rejectCandidateRequest(type, id, reason)}
        onDone={refetch}
      />

      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={dirty ? "No matching requests" : "Nothing pending"}
          message={
            dirty
              ? "No requests match the current filters."
              : `No ${meta.label.toLowerCase()} requests are currently awaiting review.`
          }
        />
      ) : (
        <Table
          columns={columnsFor(type, meta, refetch, selection)}
          data={rows}
        />
      )}
    </div>
  );
}
